<template>
  <div
    v-if="renderSwitcher"
    class="dnl:flex dnl:items-center dnl:gap-2 dnl:bg-gray-100 dnl:p-1 dnl:rounded-lg dnl:text-sm"
  >
    <button
      v-if="renderChecklist"
      :class="[
        'dnl:px-3 dnl:py-1 dnl:rounded-md  dnl:flex dnl:items-center dnl:gap-2 dnl:outline-none',
        currentType === 'checklist'
          ? 'dnl:bg-white dnl:shadow-sm dnl:text-blue-600'
          : 'dnl:text-gray-600 dnl:hover:text-blue-600',
      ]"
      data-testId="switch-checklist"
      @click="handleTypeChange('checklist')"
    >
      <dnl-icon
        icon="checklist"
        class="dnl:w-6 dnl:h-6"
      />

      <span>{{ $t('audit.quick-switch-checklist') }}</span>
    </button>
    <button
      :class="[
        'dnl:px-3 dnl:py-1 dnl:rounded-md dnl:flex dnl:items-center dnl:gap-2 dnl:outline-none',
        currentType === 'evidence'
          ? 'dnl:bg-white dnl:shadow-sm dnl:text-blue-600'
          : 'dnl:text-gray-600 dnl:hover:text-blue-600',
      ]"
      :disabled="!renderChecklist"
      data-testId="switch-numbers"
      @click="handleTypeChange('evidence')"
    >
      <dnl-icon
        icon="evidence"
        class="dnl:w-6 dnl:h-6"
      />
      <span>{{ $t('audit.quick-switch-evidence') }}</span>
    </button>
  </div>
</template>
<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from '@/utils/router';
  import { useRouter } from 'vue-router';
  import { relatedInformation } from '@/store/modules/numbers/numbers-state';

  import { AssignmentTypes } from '@/utils/assignment-process/types';
  const route = useRoute();
  const router = useRouter();

  const getCurrentMode = () => {
    const routeName = route.name as string;
    return routeName.includes('review') ? 'review' : 'audit';
  };

  const currentType = computed(() => {
    const routeName = route.name as string;
    return routeName.includes('numbers') ? 'evidence' : 'checklist';
  });

  const mode = computed(() => getCurrentMode());

  const renderChecklist = computed(() => {
    return (
      (currentType.value === 'checklist' ||
        relatedInformation.value?.productType !== AssignmentTypes.numericalAudit) &&
      route.query.numbersOnly !== 'true'
    );
  });

  /** When we don't know upfront if the assignment is numerical only - we need to hide checklist until relatedInformation with productType is set (in numbers flow) */
  const renderSwitcher = computed(() => {
    return currentType.value === 'checklist' || relatedInformation.value || route.query.numbersOnly;
  });

  const handleTypeChange = (type: 'checklist' | 'evidence') => {
    const { companyId, assignmentId } = route.params;
    const {
      createdAt,
      documentId,
      pageIndex,
      secondaryDocumentId,
      secondaryPageIndex,
      numbersOnly,
    } = route.query;

    // Common query params to preserve context
    const commonQuery = {
      createdAt,
      documentId: documentId || undefined,
      pageIndex: pageIndex || undefined,
      secondaryDocumentId: secondaryDocumentId || undefined,
      secondaryPageIndex: secondaryPageIndex || undefined,
      aiDialog: 'false', // Use aiDialog=false to skip progress dialog when switching
      numbersOnly: numbersOnly || undefined,
    };

    if (type === 'checklist') {
      const questionIndex = route.query.questionIndex || '0';
      const checklistId = route.query.checklistId;
      router.push({
        name: mode.value === 'audit' ? 'annex-questions-audit' : 'annex-questions-review',
        params: { companyId, assignmentId, questionIndex },
        query: { ...commonQuery, checklistId },
      });
    } else {
      router.push({
        name: mode.value === 'audit' ? 'annex-audit-numbers' : 'annex-review-numbers',
        params: { companyId, assignmentId },
        query: commonQuery,
      });
    }
  };
</script>
